import { Component, Inject, OnInit, PLATFORM_ID, afterNextRender, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NotificationsComponent } from './shared/notification/notification.component';
import { HeaderComponent } from './shared/header/header.component';
import { CommonModule, PlatformLocation, isPlatformBrowser } from '@angular/common';
import { MobileFooterMenuComponent } from './shared/mobile-footer-menu/mobile-footer-menu.component';
import { SignalsStoreService } from './shared/signals-store.service';
import { AppService } from './app.service';
import { ResolutionService } from './shared/resolution.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, filter, finalize, map, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { LocalStorageService } from './shared/local-storage.service';
import { LOCALSTORAGE_KEYS } from './shared/constants/databases';
import { KlaviyoService } from './shared/klaviyo/klaviyo.service';
import { ScrollToTopDirective } from './shared/directives/scroll-to-top.directive';
import { GlobalMessageComponent } from './shared/global-message/global-message.component';
import { CanonicalService } from './shared/canonical.service';
import { AttributionService } from './shared/attribution/attribution.service';
import { LoadingComponent } from './shared/loading/loading.component';
import { LoadingService } from './shared/loading.service';
import { DataLayerService } from './shared/data-layer/data-layer.service';
import { PromotionsService } from './shared/promotions.service';
import { MarketingReferral } from './shared/types/common.types';
import { checkSignupCouponCodes } from './shared/common/singup.utils';
import { sign } from 'crypto';
import { SideCheckoutComponent } from './side-checkout/side-checkout.component';
import { AccountService } from './shared/account.service';
import { OrderService } from './shared/order.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MatProgressBarModule,
    NgbModule,
    NotificationsComponent,
    HeaderComponent,
    CommonModule,
    MobileFooterMenuComponent,
    SideCheckoutComponent,
    ScrollToTopDirective,
    GlobalMessageComponent,
    LoadingComponent
  ],
  providers: [AppService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'ecommerce';

  private signalStoreService = inject(SignalsStoreService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private platformLocation = inject(PlatformLocation);
  private appService = inject(AppService);
  private resolutionService = inject(ResolutionService);
  private localStorageService = inject(LocalStorageService);
  private klaviyoService = inject(KlaviyoService);
  loadingService = inject(LoadingService);
  #dataLayerService = inject(DataLayerService);
  #promotionsService = inject(PromotionsService);
  #accountService = inject(AccountService);
  #orderService = inject(OrderService);
  #canonicalService = inject(CanonicalService);
  #attributionService = inject(AttributionService);

  private containerClassesToApply = {
    layoutSignup: 'layout-signup',
    layoutStatic: 'layout-static',
    layoutShop: 'layout-shop',
    layoutSettings: 'layout-settings',
    layoutLogin: 'layout-login',
  };

  private containerClassesXRout: any = {
    '/': this.containerClassesToApply.layoutStatic,
    '/pages': this.containerClassesToApply.layoutStatic,
    '/login': this.containerClassesToApply.layoutLogin,
    '/account-recovery': this.containerClassesToApply.layoutLogin,
    '/terms-and-conditions': this.containerClassesToApply.layoutLogin,
    '/settings': this.containerClassesToApply.layoutSettings,
    '/signup': this.containerClassesToApply.layoutSignup,
    '/shop': this.containerClassesToApply.layoutShop,
    '/favorites': this.containerClassesToApply.layoutShop,
    '/buy-again': this.containerClassesToApply.layoutShop,
    '/order': this.containerClassesToApply.layoutShop,
  }

  showAppHeader = signal(false);
  containerClassToAplly = signal('');
  isInBlankComponent = this.signalStoreService.getIsInBlankComponent;
  hasSession = this.signalStoreService.hasSession;
  #hasSession$ = toObservable(this.hasSession);
  isSidebarOpen = this.signalStoreService.isSidebarOpen;
  // Reflects the REAL global-messages state. The banner is now fetched during SSR
  // (GlobalMessageComponent fetches in its constructor, captured via TransferCache),
  // so this signal is accurate at first paint on both server and client — no CLS
  // when a message exists, and no empty 40px bar reserved when none is configured.
  hasGlobalMessages = computed(() => !!this.signalStoreService.globalMessages().length);
  showCartPreview = this.signalStoreService.showCartPreview;

  showAppCartPreview = computed(() => this.setUpCartPreviewDisplay());

  shouldShowCategoriesBar = computed(() => this.signalStoreService.shouldShowCategoriesBar());

  isMobile = computed(() => this.resolutionService.isMobile());
  routeSignal = signal(this.router.url);
  permissionsSignal = computed(() => this.signalStoreService.permissions());

  showMobileFooter = this.signalStoreService.isShowingMobileFooter;

  private isMobiles$ = toObservable(this.isMobile);
  private routeSignal$ = toObservable(this.routeSignal);
  private permissionsSignal$ = toObservable(this.permissionsSignal);

  #odooOrder$ = toObservable(this.#orderService.odooOrder);

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    // Earliest browser-side hook we have: the first navigation has not resolved
    // yet, so window.location still carries the campaign params of the landing
    // URL. The service no-ops on the server and after the first capture.
    this.#attributionService.capture();

    // Hydrate route-driven UI state synchronously so the first paint (SSR + CSR)
    // already includes the header and the correct layout class. Without this,
    // those elements are injected after the first navigation event and push
    // .main-container down → large CLS culprit on /shop.
    this.#hydrateRouteDrivenState();

    this.#identifyKlaviyoUser();
    afterNextRender(() => {
      this.appService.getMaintenanceState();
    })
  }

  #hydrateRouteDrivenState() {
    // With `enabledNonBlocking` initial navigation (Angular default for
    // provideRouter), `router.url` is "/" during AppComponent construction —
    // initial nav hasn't resolved yet. Read the real URL from PlatformLocation,
    // which is request-derived in SSR and `location.pathname` in CSR.
    const url = (this.platformLocation.pathname || '/').split('?')[0];
    this.routeSignal.set(url);

    this.showAppHeader.set(this.validateRoutesForHeader());
    this.containerClassToAplly.set(this.setUpContainerClass());
    this.signalStoreService.isInOrderComponent.set(this.validateIsInRouteComponent('/order'));

    const isShopRoute = ['/shop', '/favorites', '/buy-again'].some(r => url.includes(r));
    if (isShopRoute) this.signalStoreService.setIsInShopComponent(true);

    // Seed the categories bar from the route alone.
    //
    // The real decision lives in validateCategoriesBarDisplay(), which sits behind
    // the isPlatformBrowser guard in ngOnInit and therefore never runs on the
    // server. The signal defaults to false, so the bar was absent from the SSR
    // HTML and only appeared once the client had bootstrapped and permissions had
    // resolved. That went unnoticed while the page itself painted late; now that
    // /shop paints in ~270ms, a bar arriving ~1.4s later reads as the header
    // popping in and pushing the page down.
    //
    // Only two things can turn the bar off, and neither is knowable here: an
    // active custom-box signup (localStorage, browser-only) and a denied shop
    // permission (which defaults to allowed anyway). The client re-runs the full
    // check on its first pass and corrects the value if either applies — so the
    // worst case is the reverse of today's flash, on a path almost nobody takes,
    // instead of a shift every visitor sees.
    const barRoutes = ['/shop', '/favorites', '/buy-again', '/pages'];
    const showsCategoriesBar =
      (url === '/' || barRoutes.some(r => url.includes(r)))
      && !url.includes('/shop/custom-box/')
      && url !== '/free-eggs';
    this.signalStoreService.shouldShowCategoriesBar.set(showsCategoriesBar);
  }

  ngOnInit(): void {
    // this.appService.getMaintenanceState();
    this.setUpRouteSubscription();
    this.#canonicalService.init();

    if (!isPlatformBrowser(this.platformId)) return;
    this.klaviyoService.initLandingPageTracking();
    this.activatedRoute.queryParams.subscribe(params => this.checkMarketingReferral(params));
    this.appService.addClassToBody(environment.config.bodyClass);

    combineLatest([this.isMobiles$, this.routeSignal$, this.permissionsSignal$]).pipe(
      tap(() => this.validateCategoriesBarDisplay())
    ).subscribe()

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd)
        if (this.signalStoreService.hasSession())
          this.signalStoreService.showCartPreview.set(false);
    });

    this.#validateNewCheckoutFlowDisclaimer();
  }

  private setUpRouteSubscription() {
    // NavigationEnd only. `router.events` also fires NavigationStart, and at that
    // point `router.url` is still the PREVIOUS url — "/" on a cold load, which is
    // a header route. Reacting to it flashed the header in and straight back out on
    // routes that have none (/signup, /login), pushing the page 112px and back:
    // most of the CLS measured on /signup for ELIO-6741.
    //
    // Safe to filter now only because #hydrateRouteDrivenState() already seeded the
    // correct state from PlatformLocation during construction — otherwise the header
    // would be missing until the first NavigationEnd on routes that do show it.
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Refresh routeSignal FIRST — the helpers below now read it instead of router.url.
      this.routeSignal.set(this.router.url);
      this.showAppHeader.set(this.validateRoutesForHeader());
      // this.showCartPreview.set(this.valiteRoutesForCartPreview());
      this.containerClassToAplly.set(this.setUpContainerClass());
      this.signalStoreService.isInOrderComponent.set(this.validateIsInRouteComponent('/order'));
    });
  }

  private setUpContainerClass(): string {
    const currentUrl = this.routeSignal().split('?')[0];
    for (let key in this.containerClassesXRout) {
      if (key === '/' ? currentUrl === key : currentUrl.includes(key)) {
        return this.containerClassesXRout[key];
      }
    }

    return this.containerClassesToApply.layoutShop; // This class by default;
  }


  validateRoutesForHeader(): boolean {
    const allowedRoutes = [
      '/',
      '/order',
      '/pages',
      '/product',
      '/settings',
      '/shop',
      '/favorites',
      '/buy-again',
      '/cart',
      '/free-eggs'
    ];
    return this.validateRoutesToShowComponent(allowedRoutes);
  }

  valiteRoutesForCartPreview(): boolean {
    const allowedRoutes = [
      '/product',
      '/shop',
      '/favorites',
      '/buy-again',
      '/pages/blog-post'
    ];

    return this.validateRoutesToShowComponent(allowedRoutes, 'custom-box');
  }

  validateRoutesToShowComponent(allowedRoutes: string[], notAllowedRoute: string = 'null'): boolean {
    const currentUrl = this.routeSignal();
    const cleanedRoute = currentUrl.split('?')[0];
    return allowedRoutes.some(route => (route === '/' ? cleanedRoute === route : cleanedRoute.includes(route)) && !cleanedRoute.includes(notAllowedRoute));
  }

  validateIsInRouteComponent(route: string) {
    const currentUrl = this.routeSignal();
    const cleanedRoute = currentUrl.split('?')[0];
    return cleanedRoute === route;
  }

  private setUpCartPreviewDisplay() {
    const hasSession = this.signalStoreService.hasSession();
    const hasPaymentMethod = !!this.signalStoreService.sessionSignal()?.settings?.hasPaymentMethod;
    const shouldShowCartPreview = this.showCartPreview();
    const showInBlogPost = !this.signalStoreService.isInBlogPost() || (this.signalStoreService.isInBlogPost() && this.signalStoreService.showCartPreviewInBLogPost())
    return hasSession && hasPaymentMethod && shouldShowCartPreview && !this.isMobile() && showInBlogPost;
  }

  private validateCategoriesBarDisplay() {
    let signupCustomBox = this.localStorageService.get(LOCALSTORAGE_KEYS.SIGNUP_CUSTOM_BOX);
    if (signupCustomBox && this.routeSignal().length > 1 && !(this.routeSignal().includes('/shop/custom-box/subscription'))) {
      this.router.navigate(['/signup']);
    }
    this.signalStoreService.isCustomBoxSignupFlow.set(!!signupCustomBox);
    const isMobile = this.isMobile();

    const shouldHideHeaderCategoriesNav = !!!(this.signalStoreService.permissions()?.shop.allowed ?? true);
    if (shouldHideHeaderCategoriesNav) {
      this.signalStoreService.shouldShowCategoriesBar.set(false);
    } else {
      const shouldShowCategoriesBar = !signupCustomBox && (!isMobile || this.validateRoutesToShowComponent(['/shop', '/favorites', '/buy-again', '/', '/pages'], '/shop/custom-box/')) && this.routeSignal() !== '/free-eggs';
      this.signalStoreService.shouldShowCategoriesBar.set(shouldShowCategoriesBar || this.routeSignal() == '/maintenance');
    }

    const shouldShowMobileFooter = isMobile && !signupCustomBox && !shouldHideHeaderCategoriesNav && this.validateRoutesToShowComponent(['/shop', '/favorites', '/buy-again', '/settings', '/order', '/cart']);
    this.showMobileFooter.set(shouldShowMobileFooter);
  }

  checkMarketingReferral(params: any) {

    let marketingReferral: MarketingReferral;

    const currentMarketingInfo: MarketingReferral | null = this.localStorageService.get(LOCALSTORAGE_KEYS.MARKETING_REFERRAL);

    if (params['utm_source'] && params['utm_medium'] && params['utm_campaign']) {
      marketingReferral = {
        utmCampaign: params['utm_campaign'],
        utmMedium: params['utm_medium'],
        utmSource: params['utm_source'],
      }
    } else if (currentMarketingInfo?.utmCampaign)
      marketingReferral = { ...currentMarketingInfo };
    else return this.signalStoreService.setIsLoadedMarketingValidation(true);

    checkSignupCouponCodes({
      marketingReferral,
      signalsStoreService: this.signalStoreService,
      localStorageService: this.localStorageService,
      promotionsService: this.#promotionsService,
      showDefaultMessage: false
    }).subscribe();

  }

  #identifyKlaviyoUser() {
    this.#hasSession$.pipe(
      tap((hasSession: boolean) => {
        if (hasSession)
          this.klaviyoService.identiyUser();
      })
    ).subscribe();
  }

  #validateNewCheckoutFlowDisclaimer() {
    // If the user has a session, we check if the new checkout flow disclaimer has been accepted
    if (!this.hasSession()) return;
    // We are going to check the new checkout flow disclaimer status in every single change of the order
    this.#odooOrder$.pipe(
      tap((odooOrder) => {
        if (!odooOrder) return;
        this.#accountService.checkDisplayNewCheckoutFlowDisclaimer()
      })
    ).subscribe();
  }
}
